import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import TableContainer from "Common/TableContainer";
import { Pencil, Search, Plus, Trash2 } from "lucide-react";
import Modal from "Common/Components/Modal";
import DeleteModal from "Common/DeleteModal";
import { ToastContainer } from "react-toastify";
import { Loader } from "Common/Loader";
import { AddItem } from "./AddItem";
import { useItemList } from "./useItemList";
import { useItemModal } from "./useItemModal";
import { Common } from "helpers/common";

const ItemList = () => {
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [eventData, setEventData] = useState<any>();
    const commonHelper = new Common();

    const {
        itemList,
        isLoading,
        handleDelete,
        updateItemList,
        filterSearchData
    } = useItemList();

    const {
        show,
        isEdit,
        itemData,
        toggle,
        handleEditDataClick,
        handleAddItem
    } = useItemModal((result) => {
        updateItemList(result, isEdit, itemData);
        toggle();
    });

    document.title = "Items | GoodLuck";

    const deleteToggle = () => setDeleteModal(!deleteModal);

    const onClickDelete = (cell: any) => {
        setDeleteModal(true);
        if (cell.id) {
            setEventData(cell);
        }
    };

    const handleDeleteConfirm = async () => {
        if (eventData) {
            handleDelete(eventData.id);
            setDeleteModal(false);
        }
    };

    const columns = useMemo(() => [
        {
            header: "Item Code",
            accessorKey: "item_code",
            enableColumnFilter: true,
        },
        {
            header: "Item Name", 
            accessorKey: "item_name",
            enableColumnFilter: true,
        },
        {
            header: "Item Group",
            accessorKey: "item_group_name",
            enableColumnFilter: true,
        },
        {
            header: "Actions",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
                <div className="flex gap-3">
                    <button
                        className="flex items-center justify-center size-6 transition-all duration-200 ease-linear rounded-md edit-item-btn bg-slate-100 text-slate-500 hover:text-custom-500 hover:bg-custom-100 dark:bg-zink-600 dark:text-zink-200 dark:hover:bg-custom-500/20 dark:hover:text-custom-500"
                        onClick={() => {
                            const data = cell.row.original;
                            handleEditDataClick(data);
                        }}
                    >
                        <Pencil className="size-3" />
                    </button>
                    <button
                        className="flex items-center justify-center size-6 transition-all duration-200 ease-linear rounded-md remove-item-btn bg-red-100 text-red-500 hover:text-red-600 hover:bg-red-200 dark:bg-red-500/20 dark:text-red-500 dark:hover:bg-red-500/30 dark:hover:text-red-400"
                        onClick={() => {
                            const data = cell.row.original;
                            onClickDelete(data);
                        }}
                    >
                        <Trash2 className="size-3" />
                    </button>
                </div>
            ),
        },
    ], [handleEditDataClick]);

    return (
        <React.Fragment>
            <div style={{ marginTop: '10px' }}>
            <DeleteModal
                show={deleteModal}
                onHide={deleteToggle}
                onDelete={handleDeleteConfirm}
            />
            <ToastContainer closeButton={false} limit={1} />

            {isLoading ? (
                <Loader />
            ) : (
                <div className="card" id="itemList">
                <div className="card-body">
                    <div className="flex items-center gap-3 mb-4">
                        <h6 className="text-12 grow">Items ({itemList?.length || 0})</h6>
                        <div className="shrink-0">
                            <button
                                type="button"
                                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                                onClick={handleAddItem}
                            >
                                <Plus className="inline-block size-4" />{" "}
                                <span className="align-middle">Add Item</span>
                            </button>
                        </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <TableContainer
                            isPagination={true}
                            columns={columns || []}
                            data={itemList || []}
                            customPageSize={10}
                            divclassName="overflow-x-auto"
                            tableclassName="w-full whitespace-nowrap"
                            theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:text-zink-200 dark:bg-zink-600"
                            thclassName="px-2.5 py-0.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                            tdclassName="px-2.5 py-0.5 border-y border-slate-200 dark:border-zink-500"
                            PaginationClassName="flex flex-col items-center mt-8 md:flex-row"
                        />
                    </div>
                </div>
            </div>
            )}

            <Modal show={show} onHide={toggle} modal-center="true" className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4" dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600">
                <AddItem onHide={toggle} data={itemData} onSave={(result) => {
                    updateItemList(result, isEdit, itemData);
                    toggle();
                }} />
            </Modal>
            </div>
        </React.Fragment>
    );
};

export default ItemList;